import React, { useState, useEffect, useCallback } from 'react';

/**
 * 거래내역 분석 기능 모달
 * - 탭1(새 분석): 기본기간 확인/수정 후 '분석시작'으로 실행
 * - 탭2(분석 기록): 저장된 분석 기록 리스트 열람
 */
const TradeHistoryAnalysisModal = ({ onClose, authenticatedFetch, startDate: initialStart, endDate: initialEnd }) => {
  const [activeTab, setActiveTab] = useState('run'); // 'run' | 'records'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [expandedBySymbol, setExpandedBySymbol] = useState(true);
  const [expandedBySource, setExpandedBySource] = useState(true);
  const [expandedTradeTimeline, setExpandedTradeTimeline] = useState(false);
  const [bySymbolFilter, setBySymbolFilter] = useState('all'); // 'all' | 'profit_only'

  const todayStr = new Date().toISOString().slice(0, 10);
  const monthStart = (() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  })();
  const [runStart, setRunStart] = useState(initialStart || monthStart);
  const [runEnd, setRunEnd] = useState(initialEnd || todayStr);

  const [records, setRecords] = useState([]);
  const [recordsLoading, setRecordsLoading] = useState(false);
  const [selectedRecordId, setSelectedRecordId] = useState(null);
  const [recordDetail, setRecordDetail] = useState(null);
  const [recordDetailLoading, setRecordDetailLoading] = useState(false);

  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);

  useEffect(() => {
    setRunStart(initialStart || monthStart);
    setRunEnd(initialEnd || todayStr);
  }, [initialStart, initialEnd, monthStart, todayStr]);

  const fetchRecords = useCallback(() => {
    if (!authenticatedFetch) return;
    setRecordsLoading(true);
    authenticatedFetch('/api/statistics/analysis-records', { timeout: 30000 })
      .then((res) => res.ok ? res.json() : Promise.reject(new Error('조회 실패')))
      .then((json) => { setRecords(json.records || []); setSelectedRecordId(null); setRecordDetail(null); })
      .catch((err) => setRecords([]))
      .finally(() => setRecordsLoading(false));
  }, [authenticatedFetch]);

  useEffect(() => {
    if (activeTab === 'records') fetchRecords();
  }, [activeTab, fetchRecords]);

  const deleteRecord = useCallback((id, e) => {
    if (e) e.stopPropagation();
    if (!authenticatedFetch || !id || !window.confirm('이 분석 기록을 삭제하시겠습니까?')) return;
    authenticatedFetch(`/api/statistics/analysis-records/${id}`, { method: 'DELETE', timeout: 10000 })
      .then((res) => {
        if (res.ok) {
          if (selectedRecordId === id) { setSelectedRecordId(null); setRecordDetail(null); setAiAnalysis(null); }
          fetchRecords();
        } else return res.json().then((body) => { throw new Error(body?.error || '삭제 실패'); });
      })
      .catch((err) => window.alert(err?.message || '삭제에 실패했습니다.'));
  }, [authenticatedFetch, selectedRecordId, fetchRecords]);

  const fetchRecordDetail = useCallback((id) => {
    if (!authenticatedFetch || !id) return;
    setSelectedRecordId(id);
    setRecordDetailLoading(true);
    setRecordDetail(null);
    setAiAnalysis(null);
    setAiError(null);
    authenticatedFetch(`/api/statistics/analysis-records/${id}`)
      .then((res) => {
        if (res.ok) return res.json();
        throw new Error('조회 실패');
      })
      .then((json) => {
        setRecordDetail(json);
        if (json.ai_analysis) setAiAnalysis(json.ai_analysis);
      })
      .catch(() => setRecordDetail(null))
      .finally(() => setRecordDetailLoading(false));
  }, [authenticatedFetch]);

  const runAnalysis = useCallback(() => {
    if (!authenticatedFetch || !runStart || !runEnd) {
      setError('기간이 지정되지 않았습니다.');
      return;
    }
    if (runStart > runEnd) {
      setError('시작일은 종료일보다 이전이어야 합니다.');
      return;
    }
    setLoading(true);
    setError(null);
    authenticatedFetch('/api/statistics/history-analysis', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ startDate: runStart, endDate: runEnd }),
      timeout: 60000
    })
      .then((res) => {
        if (res.ok) return res.json();
        return res.json().catch(() => ({})).then((body) => {
          throw new Error(body?.error || res.statusText || `실패 (${res.status})`);
        });
      })
      .then((json) => {
        setData({ period: json.period, summary: json.summary, bySymbol: json.bySymbol || [], bySource: json.bySource || [], balance: json.balance, tradeTimeline: json.tradeTimeline || [], realizedPLByTrade: json.realizedPLByTrade || [], recordId: json.recordId });
        setError(null);
        setAiAnalysis(null);
        setAiError(null);
        fetchRecords();
      })
      .catch((err) => {
        setError(err?.message || '분석 실행에 실패했습니다.');
        setData(null);
      })
      .finally(() => setLoading(false));
  }, [authenticatedFetch, runStart, runEnd, fetchRecords]);

  const formatAmount = (v) => (v != null ? `₩${Number(v).toLocaleString()}` : '-');
  const formatRate = (v) => (v != null ? `${v >= 0 ? '+' : ''}${Number(v).toFixed(2)}%` : '-');
  const displayData = data || recordDetail;

  const runAiAnalysis = useCallback(() => {
    if (!authenticatedFetch || !displayData) return;
    setAiLoading(true);
    setAiError(null);
    setAiAnalysis(null);
    const recordId = displayData.recordId || selectedRecordId;
    const payload = displayData.period?.startDate && displayData.period?.endDate
      ? { startDate: displayData.period.startDate, endDate: displayData.period.endDate }
      : { analysisData: displayData };
    if (recordId) payload.recordId = recordId;
    authenticatedFetch('/api/statistics/ai-trade-analysis', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      timeout: 60000
    })
      .then((res) => {
        if (res.ok) return res.json();
        return res.json().catch(() => ({})).then((body) => {
          throw new Error(body?.error || res.statusText || 'AI 분석 실패');
        });
      })
      .then((json) => {
        setAiAnalysis(json.analysis || '');
        setAiError(null);
        if (json.saved) fetchRecords();
      })
      .catch((err) => {
        setAiError(err?.message || 'AI(거래) 분석에 실패했습니다.');
        setAiAnalysis(null);
      })
      .finally(() => setAiLoading(false));
  }, [authenticatedFetch, displayData, selectedRecordId, fetchRecords]);

  const renderResult = (d) => (
    <>
      <div style={{ marginBottom: '12px', fontSize: '12px', color: '#666' }}>기간: {d.period?.startDate} ~ {d.period?.endDate}</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: '10px', marginBottom: '16px' }}>
        <div style={{ padding: '10px 12px', backgroundColor: '#f5f5f5', borderRadius: '8px', textAlign: 'center' }}>
          <div style={{ fontSize: '11px', color: '#666' }}>총 거래</div>
          <div style={{ fontSize: '16px', fontWeight: 700 }}>{d.summary?.totalTrades ?? 0}건</div>
        </div>
        <div style={{ padding: '10px 12px', backgroundColor: '#e8f5e9', borderRadius: '8px', textAlign: 'center' }}>
          <div style={{ fontSize: '11px', color: '#666' }}>시작 잔고</div>
          <div style={{ fontSize: '12px', fontWeight: 700, color: '#2e7d32' }}>{formatAmount(d.summary?.startBalance)}</div>
        </div>
        <div style={{ padding: '10px 12px', backgroundColor: '#e8f5e9', borderRadius: '8px', textAlign: 'center' }}>
          <div style={{ fontSize: '11px', color: '#666' }}>종료 잔고</div>
          <div style={{ fontSize: '12px', fontWeight: 700, color: '#2e7d32' }}>{formatAmount(d.summary?.endBalance)}</div>
        </div>
        <div style={{ padding: '10px 12px', backgroundColor: (d.summary?.profitRateFromBalance ?? d.summary?.profitRate ?? 0) >= 0 ? '#e8f5e9' : '#ffebee', borderRadius: '8px', textAlign: 'center' }}>
          <div style={{ fontSize: '11px', color: '#666' }}>수익(총액 변동)</div>
          <div style={{ fontSize: '13px', fontWeight: 700, color: ((d.summary?.profitRateFromBalance ?? d.summary?.profitRate) || 0) >= 0 ? '#2e7d32' : '#c62828' }}>{formatAmount(d.summary?.profitLossFromBalance ?? d.summary?.profitLoss)} {formatRate(d.summary?.profitRateFromBalance ?? d.summary?.profitRate)}</div>
        </div>
        <div style={{ padding: '10px 12px', backgroundColor: '#e3f2fd', borderRadius: '8px', textAlign: 'center' }}>
          <div style={{ fontSize: '11px', color: '#666' }}>매수/매도</div>
          <div style={{ fontSize: '12px', fontWeight: 600, color: '#1976d2' }}>{formatAmount(d.summary?.buyAmount)} / {formatAmount(d.summary?.sellAmount)}</div>
        </div>
        <div style={{ padding: '10px 12px', backgroundColor: '#f3e5f5', borderRadius: '8px', textAlign: 'center' }}>
          <div style={{ fontSize: '11px', color: '#666' }}>AI vs 일반</div>
          <div style={{ fontSize: '13px', fontWeight: 600 }}>{d.summary?.aiTradeCount ?? 0} / {d.summary?.manualTradeCount ?? 0}건</div>
        </div>
      </div>
      {(d.summary?.startBalance != null || d.balance?.change) && (
        <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#fafafa', borderRadius: '8px', border: '1px solid #eee' }}>
          <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '10px', color: '#333' }}>📈 총액 변동</div>
          <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
            <thead><tr style={{ borderBottom: '1px solid #ddd' }}><th style={{ padding: '6px 8px', textAlign: 'left' }}>구분</th><th style={{ padding: '6px 8px', textAlign: 'right' }}>시작 시점</th><th style={{ padding: '6px 8px', textAlign: 'right' }}>종료 시점</th><th style={{ padding: '6px 8px', textAlign: 'right' }}>변동(Δ)</th></tr></thead>
            <tbody>
              <tr style={{ borderBottom: '1px solid #eee' }}>
                <td style={{ padding: '6px 8px' }}>현금</td>
                <td style={{ padding: '6px 8px', textAlign: 'right' }}>{formatAmount(d.summary?.startCash)}</td>
                <td style={{ padding: '6px 8px', textAlign: 'right' }}>{formatAmount(d.summary?.endCash)}</td>
                <td style={{ padding: '6px 8px', textAlign: 'right', color: (d.summary?.cashChange ?? 0) >= 0 ? '#2e7d32' : '#c62828' }}>{(d.summary?.cashChange ?? d.balance?.change?.cash) != null ? `${(d.summary?.cashChange ?? d.balance?.change?.cash) >= 0 ? '+' : ''}${formatAmount(d.summary?.cashChange ?? d.balance?.change?.cash)}` : '-'}</td>
              </tr>
              <tr style={{ borderBottom: '1px solid #eee' }}>
                <td style={{ padding: '6px 8px' }}>주식평가액</td>
                <td style={{ padding: '6px 8px', textAlign: 'right' }}>{formatAmount(d.summary?.startStockValue)}</td>
                <td style={{ padding: '6px 8px', textAlign: 'right' }}>{formatAmount(d.summary?.endStockValue)}</td>
                <td style={{ padding: '6px 8px', textAlign: 'right', color: (d.summary?.stockValueChange ?? 0) >= 0 ? '#2e7d32' : '#c62828' }}>{(d.summary?.stockValueChange ?? d.balance?.change?.stockValue) != null ? `${(d.summary?.stockValueChange ?? d.balance?.change?.stockValue) >= 0 ? '+' : ''}${formatAmount(d.summary?.stockValueChange ?? d.balance?.change?.stockValue)}` : '-'}</td>
              </tr>
              <tr style={{ fontWeight: 600 }}>
                <td style={{ padding: '8px' }}>총액</td>
                <td style={{ padding: '8px', textAlign: 'right' }}>{formatAmount(d.summary?.startBalance)}</td>
                <td style={{ padding: '8px', textAlign: 'right' }}>{formatAmount(d.summary?.endBalance)}</td>
                <td style={{ padding: '8px', textAlign: 'right', color: (d.summary?.profitLossFromBalance ?? 0) >= 0 ? '#2e7d32' : '#c62828' }}>{(d.summary?.profitLossFromBalance ?? d.balance?.change?.total) != null ? `${(d.summary?.profitLossFromBalance ?? d.balance?.change?.total) >= 0 ? '+' : ''}${formatAmount(d.summary?.profitLossFromBalance ?? d.balance?.change?.total)}` : '-'}</td>
              </tr>
            </tbody>
          </table>
          {d.summary?.initialBalance != null && <div style={{ fontSize: '11px', color: '#999', marginTop: '6px' }}>※ 초기 잔고(기준): {formatAmount(d.summary.initialBalance)}</div>}
        </div>
      )}
      {((d.tradeTimeline && d.tradeTimeline.length > 0) || (d.realizedPLByTrade && d.realizedPLByTrade.length > 0)) && (
        <div style={{ marginBottom: '16px' }}>
          <button type="button" onClick={() => setExpandedTradeTimeline(!expandedTradeTimeline)} style={{ width: '100%', padding: '8px 12px', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>
            거래 시점별 손익 {expandedTradeTimeline ? '접기' : '펼치기'}
          </button>
          {expandedTradeTimeline && (
            <>
              {d.tradeTimeline && d.tradeTimeline.length > 0 && (
                <div style={{ marginTop: '8px', overflowX: 'auto' }}>
                  <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
                    <thead><tr style={{ backgroundColor: '#f0f0f0' }}><th style={{ padding: '6px 8px', textAlign: 'left' }}>시점</th><th style={{ padding: '6px 8px' }}>유형</th><th style={{ padding: '6px 8px' }}>종목</th><th style={{ padding: '6px 8px', textAlign: 'right' }}>수량</th><th style={{ padding: '6px 8px', textAlign: 'right' }}>금액</th><th style={{ padding: '6px 8px', textAlign: 'right' }}>직후 총액</th><th style={{ padding: '6px 8px', textAlign: 'right' }}>실현손익</th></tr></thead>
                    <tbody>
                      {d.tradeTimeline.map((row, i) => (
                        <tr key={row.tradeId || i} style={{ borderBottom: '1px solid #eee' }}>
                          <td style={{ padding: '6px 8px' }}>{row.date} {row.time}</td>
                          <td style={{ padding: '6px 8px' }}>{row.orderType === 'sell' ? '매도' : '매수'}</td>
                          <td style={{ padding: '6px 8px' }}>{row.symbol}{row.stockName && row.stockName !== row.symbol ? ` (${row.stockName})` : ''}</td>
                          <td style={{ padding: '6px 8px', textAlign: 'right' }}>{row.quantity}</td>
                          <td style={{ padding: '6px 8px', textAlign: 'right' }}>{formatAmount(row.amount)}</td>
                          <td style={{ padding: '6px 8px', textAlign: 'right' }}>{formatAmount(row.balanceAfter?.totalAssets)}</td>
                          <td style={{ padding: '6px 8px', textAlign: 'right', color: row.realizedPL != null ? (row.realizedPL >= 0 ? '#2e7d32' : '#c62828') : 'inherit', fontWeight: row.realizedPL != null ? 600 : 400 }}>{row.realizedPL != null ? formatAmount(row.realizedPL) : '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {d.realizedPLByTrade && d.realizedPLByTrade.length > 0 && (
                <div style={{ marginTop: '12px', fontSize: '12px', fontWeight: 600, color: '#333' }}>매도별 실현손익</div>
              )}
              {d.realizedPLByTrade && d.realizedPLByTrade.length > 0 && (
                <table style={{ width: '100%', marginTop: '6px', fontSize: '12px', borderCollapse: 'collapse' }}>
                  <thead><tr style={{ backgroundColor: '#f0f0f0' }}><th style={{ padding: '6px 8px', textAlign: 'left' }}>종목</th><th style={{ padding: '6px 8px', textAlign: 'right' }}>매도금액</th><th style={{ padding: '6px 8px', textAlign: 'right' }}>매수원가</th><th style={{ padding: '6px 8px', textAlign: 'right' }}>실현손익</th></tr></thead>
                  <tbody>
                    {d.realizedPLByTrade.map((r, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '6px 8px' }}>{r.symbol}{r.stockName && r.stockName !== r.symbol ? ` (${r.stockName})` : ''}</td>
                        <td style={{ padding: '6px 8px', textAlign: 'right' }}>{formatAmount(r.sellAmount)}</td>
                        <td style={{ padding: '6px 8px', textAlign: 'right' }}>{formatAmount(r.costBasis)}</td>
                        <td style={{ padding: '6px 8px', textAlign: 'right', color: (r.realizedPL || 0) >= 0 ? '#2e7d32' : '#c62828', fontWeight: 600 }}>{formatAmount(r.realizedPL)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
        </div>
      )}
      <div style={{ marginBottom: '12px' }}>
        <button type="button" onClick={() => setExpandedBySource(!expandedBySource)} style={{ width: '100%', padding: '8px 12px', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>
          유형별 {expandedBySource ? '접기' : '펼치기'}
        </button>
        {expandedBySource && (d.bySource || []).length > 0 && (
          <table style={{ width: '100%', marginTop: '6px', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead><tr style={{ backgroundColor: '#f0f0f0' }}><th style={{ padding: '6px 10px', textAlign: 'left', border: '1px solid #ddd' }}>유형</th><th style={{ padding: '6px 10px', textAlign: 'right', border: '1px solid #ddd' }}>건수</th><th style={{ padding: '6px 10px', textAlign: 'right', border: '1px solid #ddd' }}>금액</th></tr></thead>
            <tbody>
              {(d.bySource || []).map((r, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: '6px 10px', border: '1px solid #eee' }}>{r.source === 'ai-trade' ? '🤖 AI' : '📝 일반'}</td>
                  <td style={{ padding: '6px 10px', textAlign: 'right', border: '1px solid #eee' }}>{r.count}건</td>
                  <td style={{ padding: '6px 10px', textAlign: 'right', border: '1px solid #eee' }}>{formatAmount(r.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <div>
        <button type="button" onClick={() => setExpandedBySymbol(!expandedBySymbol)} style={{ width: '100%', padding: '8px 12px', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>
          종목별 {expandedBySymbol ? '접기' : '펼치기'}
        </button>
        {expandedBySymbol && (d.bySymbol || []).length > 0 && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '6px', marginBottom: '6px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '12px', color: '#666' }}>필터:</span>
              <button type="button" onClick={() => setBySymbolFilter('all')} style={{ padding: '4px 10px', fontSize: '12px', border: '1px solid #dee2e6', borderRadius: '6px', cursor: 'pointer', backgroundColor: bySymbolFilter === 'all' ? '#667eea' : '#fff', color: bySymbolFilter === 'all' ? '#fff' : '#333' }}>전체</button>
              <button type="button" onClick={() => setBySymbolFilter('profit_only')} style={{ padding: '4px 10px', fontSize: '12px', border: '1px solid #dee2e6', borderRadius: '6px', cursor: 'pointer', backgroundColor: bySymbolFilter === 'profit_only' ? '#667eea' : '#fff', color: bySymbolFilter === 'profit_only' ? '#fff' : '#333' }}>수익만</button>
              <span style={{ fontSize: '11px', color: '#999' }}>· 수익률 높은 순 정렬</span>
            </div>
            <table style={{ width: '100%', marginTop: '0', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead><tr style={{ backgroundColor: '#f0f0f0' }}><th style={{ padding: '6px 10px' }}>종목</th><th style={{ padding: '6px 10px' }}>매수</th><th style={{ padding: '6px 10px' }}>매도</th><th style={{ padding: '6px 10px' }}>수익률</th></tr></thead>
              <tbody>
                {(() => {
                  const list = (d.bySymbol || [])
                    .filter(r => bySymbolFilter === 'all' || (r.profitRate != null && r.profitRate >= 0))
                    .sort((a, b) => (b.profitRate ?? -Infinity) - (a.profitRate ?? -Infinity));
                  return list.slice(0, 25).map((r, i) => (
                    <tr key={i}><td style={{ padding: '6px 10px' }}>{r.symbol} {r.name && r.name !== r.symbol ? `(${r.name})` : ''}</td><td style={{ padding: '6px 10px' }}>{r.buyCount}건</td><td style={{ padding: '6px 10px' }}>{r.sellCount}건</td><td style={{ padding: '6px 10px', color: (r.profitRate || 0) >= 0 ? '#2e7d32' : '#c62828' }}>{formatRate(r.profitRate)}</td></tr>
                  ));
                })()}
              </tbody>
            </table>
          </>
        )}
      </div>
      {(d.period || d.summary) && (
        <div style={{ marginTop: '16px', paddingTop: '12px', borderTop: '1px solid #eee' }}>
          <button type="button" onClick={runAiAnalysis} disabled={aiLoading || !displayData} style={{ padding: '8px 16px', fontSize: '13px', fontWeight: 600, backgroundColor: (aiLoading || !displayData) ? '#ccc' : '#9c27b0', color: '#fff', border: 'none', borderRadius: '8px', cursor: (aiLoading || !displayData) ? 'not-allowed' : 'pointer' }} title={!displayData ? '분석 데이터 선택 후 사용' : 'AI 거래분석'}>
            {aiLoading ? 'AI 분석 중...' : 'AI 거래분석'}
          </button>
          {aiError && <p style={{ color: '#d32f2f', marginTop: '8px', fontSize: '13px' }}>{aiError}</p>}
          {aiAnalysis && <div style={{ marginTop: '12px', padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '8px', fontSize: '14px', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{aiAnalysis}</div>}
        </div>
      )}
    </>
  );

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 10000, padding: '20px' }} onClick={onClose}>
      <div style={{ backgroundColor: '#fff', borderRadius: '12px', padding: '20px', maxWidth: '700px', width: '100%', maxHeight: '90vh', overflow: 'auto', boxShadow: '0 4px 20px rgba(0,0,0,0.2)' }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', borderBottom: '2px solid #e0e0e0', paddingBottom: '12px' }}>
          <h2 style={{ margin: 0, fontSize: '17px' }}>📊 분석기능</h2>
          <button type="button" onClick={onClose} style={{ background: '#f44336', color: '#fff', border: 'none', borderRadius: '6px', width: '30px', height: '30px', cursor: 'pointer', fontSize: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
        </div>

        {/* 탭: 새 분석 | 분석 기록 */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
          <button type="button" onClick={() => { setActiveTab('run'); setData(null); setError(null); }} style={{ padding: '8px 16px', fontSize: '14px', fontWeight: 600, border: '1px solid #dee2e6', borderRadius: '8px', cursor: 'pointer', backgroundColor: activeTab === 'run' ? '#667eea' : '#fff', color: activeTab === 'run' ? '#fff' : '#333' }}>새 분석</button>
          <button type="button" onClick={() => setActiveTab('records')} style={{ padding: '8px 16px', fontSize: '14px', fontWeight: 600, border: '1px solid #dee2e6', borderRadius: '8px', cursor: 'pointer', backgroundColor: activeTab === 'records' ? '#667eea' : '#fff', color: activeTab === 'records' ? '#fff' : '#333' }}>분석 기록</button>
        </div>

        {activeTab === 'run' && (
          <>
            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontSize: '13px', color: '#666', marginBottom: '8px' }}>기본기간 확인 및 수정</div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                <input type="date" value={runStart} onChange={(e) => setRunStart(e.target.value)} max={runEnd || todayStr} style={{ padding: '6px 10px', fontSize: '13px', borderRadius: '6px', border: '1px solid #ddd' }} />
                <span>~</span>
                <input type="date" value={runEnd} onChange={(e) => setRunEnd(e.target.value)} min={runStart} max={todayStr} style={{ padding: '6px 10px', fontSize: '13px', borderRadius: '6px', border: '1px solid #ddd' }} />
                <button type="button" onClick={runAnalysis} disabled={loading || !runStart || !runEnd} style={{ padding: '8px 20px', fontSize: '14px', fontWeight: 700, backgroundColor: (loading || !runStart || !runEnd) ? '#ccc' : '#28a745', color: '#fff', border: 'none', borderRadius: '8px', cursor: (loading || !runStart || !runEnd) ? 'not-allowed' : 'pointer' }}>
                  {loading ? '분석 중...' : '분석시작'}
                </button>
                <button type="button" onClick={runAiAnalysis} disabled={aiLoading || !data} title={!data ? '분석시작 후 사용 가능' : 'AI 거래분석'} style={{ padding: '8px 20px', fontSize: '14px', fontWeight: 600, backgroundColor: (aiLoading || !data) ? '#ccc' : '#9c27b0', color: '#fff', border: 'none', borderRadius: '8px', cursor: (aiLoading || !data) ? 'not-allowed' : 'pointer' }}>
                  {aiLoading ? 'AI 분석 중...' : 'AI 거래분석'}
                </button>
              </div>
            </div>
            {error && <p style={{ color: '#d32f2f', padding: '10px', backgroundColor: '#ffebee', borderRadius: '8px', marginBottom: '12px', fontSize: '13px' }}>{error}</p>}
            {loading && <p style={{ textAlign: 'center', color: '#666', padding: '30px' }}>분석 실행 중...</p>}
            {!loading && data && renderResult(data)}
          </>
        )}

        {activeTab === 'records' && (
          <>
            {recordsLoading ? (
              <p style={{ textAlign: 'center', color: '#666', padding: '30px' }}>기록 불러오는 중...</p>
            ) : records.length === 0 ? (
              <p style={{ textAlign: 'center', color: '#999', padding: '30px', fontSize: '14px' }}>저장된 분석 기록이 없습니다. 새 분석 탭에서 분석시작을 실행해주세요.</p>
            ) : (
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 200px', minWidth: '200px', maxHeight: '280px', overflowY: 'auto', border: '1px solid #eee', borderRadius: '8px', padding: '8px' }}>
                  <div style={{ fontSize: '12px', fontWeight: 600, marginBottom: '8px', color: '#666' }}>기록 목록</div>
                  {records.map((r) => (
                    <div key={r._id} onClick={() => fetchRecordDetail(r._id)} style={{ padding: '10px 12px', marginBottom: '6px', backgroundColor: selectedRecordId === r._id ? '#e3f2fd' : '#f8f9fa', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', border: selectedRecordId === r._id ? '2px solid #1976d2' : '1px solid #eee', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600 }}>{r.start_date} ~ {r.end_date}</div>
                        <div style={{ fontSize: '12px', color: '#666' }}>거래 {r.summary?.totalTrades ?? 0}건 · 수익률 {formatRate(r.summary?.profitRate)}{r.ai_analysis ? ' · AI 분석 있음' : ''}</div>
                        <div style={{ fontSize: '11px', color: '#999' }}>{r.createdAt ? new Date(r.createdAt).toLocaleString('ko-KR') : ''}</div>
                      </div>
                      <button type="button" onClick={(e) => deleteRecord(r._id, e)} style={{ flexShrink: 0, width: '24px', height: '24px', padding: 0, fontSize: '14px', lineHeight: 1, color: '#999', backgroundColor: 'transparent', border: '1px solid #ddd', cursor: 'pointer', borderRadius: '4px' }} title="삭제">[×]</button>
                    </div>
                  ))}
                </div>
                <div style={{ flex: '1 1 300px', minWidth: '280px', border: '1px solid #eee', borderRadius: '8px', padding: '12px', minHeight: '200px' }}>
                  {!selectedRecordId ? (
                    <p style={{ color: '#999', textAlign: 'center', padding: '40px 0', fontSize: '14px' }}>← 목록에서 기록을 선택하세요</p>
                  ) : recordDetailLoading ? (
                    <p style={{ textAlign: 'center', color: '#666', padding: '40px' }}>상세 불러오는 중...</p>
                  ) : recordDetail ? (
                    renderResult(recordDetail)
                  ) : (
                    <p style={{ color: '#d32f2f', fontSize: '13px' }}>상세를 불러올 수 없습니다.</p>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        <div style={{ marginTop: '16px', paddingTop: '12px', borderTop: '1px solid #eee', display: 'flex', justifyContent: 'flex-end' }}>
          <button type="button" onClick={onClose} style={{ padding: '8px 20px', backgroundColor: '#667eea', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 600 }}>닫기</button>
        </div>
      </div>
    </div>
  );
};

export default TradeHistoryAnalysisModal;
